import React, { Component } from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import {
  Ctrl,
  CtrlCollection,
  CtrlStates,
  CtrlType
} from "@yuigoto/ctrl";

/**
 * Components/Renderers/NextControlRenderer
 * ----------------------------------------------------------------------
 */
export class NextControlRenderer extends Component {
  /**
   *
   */
  render () {
    const { props } = this;
    const { control } = props;

    let _cls = ["form-group"];

    return (
      <div className={classnames(_cls, props.className, control.wrapClass)}>
        {this._renderMainLabel(control)}
        {this._renderDescription(control)}
        {this._renderInput(control)}
        {
          control.message !== ""
          && control.type !== CtrlType.SINGLE_OPTION
          && control.type !== CtrlType.RADIO_GROUP
          && control.type !== CtrlType.MULTIPLE_OPTION
          && control.type !== CtrlType.CHECKBOX_GROUP
          && this._renderFeedback(control)
        }
      </div>
    );
  }

  // --------------------------------------------------------------------

  static renderCollection (collection) {
    if (collection instanceof CtrlCollection) {
      let rendered = [];
      for (let i = 0; i < collection.collection.length; i++) {
        if (collection.collection[i] instanceof CtrlCollection) {
          rendered.push(
            <div className={"form-row"} key={i}>
              {NextControlRenderer.renderCollection(collection.collection[i])}
            </div>
          );
        } else {
          rendered.push(
            <NextControlRenderer key={i} control={collection.collection[i]}/>
          );
        }
      }
      return rendered;
    }
    return null;
  }

  // --------------------------------------------------------------------

  /**
   *
   * @param {Ctrl} control
   */
  _renderMainLabel (control) {
    if (control.type !== CtrlType.BOOLEAN) {
      let info = (control.info)
        ? (<small>{control.info}</small>)
        : null,
        required = (control.required)
          ? "*"
          : null;

      return (
        <label>
          {control.label}{required} {info}
        </label>
      );
    }
    return null;
  }

  /**
   *
   * @param {Ctrl} control
   */
  _renderInput (control) {
    switch (control.type) {
      case CtrlType.MULTIPLE_OPTION:
      case CtrlType.CHECKBOX_GROUP:
        return this._inputCheckboxGroup(control);
      case CtrlType.SINGLE_OPTION:
      case CtrlType.RADIO_GROUP:
        return this._inputRadioGroup(control);
      case CtrlType.TEXTAREA:
        return this._inputTextArea(control);
      case CtrlType.DROPDOWN:
        return this._inputDropdown(control);
      case CtrlType.BOOLEAN:
        return this._inputBoolean(control);
      default:
        return this._inputDefault(control);
    }
  }

  /**
   *
   * @param {Ctrl} control
   */
  _renderDescription (control) {
    if (control.type !== CtrlType.BOOLEAN) {
      if (control.description) {
        return (
          <p className={"form-info"}>{control.description}</p>
        );
      }
    }
    return null;
  }

  /**
   *
   * @param {Ctrl} control
   */
  _renderFeedback (control) {
    let _cls = ["form-feedback"];
    if (control.state === CtrlStates.ERROR) _cls.push("invalid-feedback");
    if (control.state === CtrlStates.VALID) _cls.push("valid-feedback");

    return (
      <div className={classnames(_cls)}>
        {control.message}
      </div>
    );
  }

  /**
   *
   * @param {Ctrl} control
   */
  _inputDefault (control) {
    let _cls = ["form-control"];
    if (control.state === CtrlStates.ERROR) _cls.push("is-invalid");
    if (control.state === CtrlStates.VALID) _cls.push("is-valid");

    let inputObject = {
      className: classnames(_cls, control.customClass),
      value: control.value || "",
      type: control.getType(),
      placeholder: control.placeholder,
      state: control.state,
      onChange: e => control.onValueChange(e.target.value) || this.forceUpdate()
    };

    if (control.autocomplete) {
      inputObject.autoComplete = control.autocomplete
    }

    return (
      <input {...inputObject}/>
    );
  }

  /**
   *
   * @param {Ctrl} control
   */
  _inputBoolean (control) {
    let { value } = control;

    let _id = control.name + "-" + (new Date()).toString(),
      _checked = (value === true),
      _clsInput,
      _clsCheck,
      _clsLabel;

    if (control.custom) {
      _clsInput = ["custom-toggle"];
      _clsCheck = ["custom-toggle-input"];
      _clsLabel = ["custom-toggle-label"];
    } else {
      _clsInput = ["form-check"];
      _clsCheck = ["form-check-input"];
      _clsLabel = ["form-check-label"];
    }

    if (control.state === CtrlStates.ERROR) _clsCheck.push("is-invalid");
    if (control.state === CtrlStates.VALID) _clsCheck.push("is-valid");
    
    if (control.custom) {
      return (
        <div className={classnames(_clsInput, control.customClass)}>
          <div className={classnames(_clsCheck)}>
            <input
              id={_id}
              type={control.getType()}
              name={control.name}
              checked={_checked}
              onChange={e => control.onBooleanChange(value) || this.forceUpdate()}
              aria-invalid={control.state === CtrlStates.ERROR}
              disabled={control.disabled}/>
            <label htmlFor={_id} className={classnames(_clsLabel)}>
              {control.label}
            </label>
          </div>
          <p className={"form-info"}>
            {control.description}
          </p>
        </div>
      );
    }

    return (
      <div className={classnames(_clsInput, control.customClass)}>
        <input
          id={_id}
          className={classnames(_clsCheck)}
          type={control.getType()}
          name={control.name}
          checked={_checked}
          onChange={e => control.onBooleanChange(value) || this.forceUpdate()}
          aria-invalid={control.state === CtrlStates.ERROR}
          disabled={control.disabled}/>
        <label htmlFor={_id} className={classnames(_clsLabel)}>
          {control.label}
        </label>
      </div>
    );
  }

  /**
   *
   * @param {Ctrl} control
   */
  _inputCheckboxGroup (control) {
    let { options, value } = control;
    
    let optionGroup = options.map((item, key) => {
      let _id = `${control.name}-checkbox-${key}`,
        _checked,
        _click,
        _clsInput,
        _clsCheck,
        _clsLabel;

      if (item.custom === true) {
        _clsLabel = ["custom-control-label"];
        _clsCheck = ["custom-control-input"];
        _clsInput = ["custom-control custom-checkbox"];
      } else {
        _clsLabel = ["form-check-label"];
        _clsCheck = ["form-check-input"];
        _clsInput = ["form-check"];
        if (item.inline === true) _clsInput.push("form-check-inline");
      }

      if (control.state === CtrlStates.ERROR) _clsCheck.push("is-invalid");
      if (control.state === CtrlStates.VALID) _clsCheck.push("is-valid");

      _checked = (
        Array.isArray(value)
        && (value.includes(item.value) || value.includes(item.name))
      );

      _click = () => {
        control.onValueToggle(item.value || item.name) || this.forceUpdate();
      };
    
      if (item.custom) {
        return (
          <div key={key}  className={classnames(_clsInput, control.customClass)}>
            <div className={classnames(_clsCheck)}>
              <input
                id={_id}
                type={control.getType()}
                name={control.name}
                checked={_checked}
                onChange={_click}
                aria-invalid={control.state === CtrlStates.ERROR}
                disabled={item.disabled}/>
              <label htmlFor={_id} className={classnames(_clsLabel)}>
                {item.name}
              </label>
            </div>
          </div>
        );
      }

      return (
        <div key={key} className={classnames(_clsInput, control.customClass)}>
          <input
            id={_id}
            className={classnames(_clsCheck)}
            type={control.getType()}
            name={control.name}
            checked={_checked}
            onChange={_click}
            aria-invalid={control.state === CtrlStates.ERROR}
            disabled={item.disabled}/>
          <label htmlFor={_id} className={classnames(_clsLabel)}>
            {item.name}
          </label>
        </div>
      );
    });

    return options && optionGroup;
  }

  /**
   *
   * @param {Ctrl} control
   */
  _inputRadioGroup (control) {
    let { options, value } = control;
    let optionGroup = options.map((item, key) => {
      let _id = `${control.name}-radio-${key}`,
        _checked,
        _click,
        _clsInput,
        _clsCheck,
        _clsLabel;

      if (item.custom === true) {
        _clsLabel = ["custom-control-label"];
        _clsCheck = ["custom-control-input"];
        _clsInput = ["custom-control custom-radio"];
      } else {
        _clsLabel = ["form-check-label"];
        _clsCheck = ["form-check-input"];
        _clsInput = ["form-check"];
        if (item.inline === true) _clsInput.push("form-check-inline");
      }

      if (control.state === CtrlStates.ERROR) _clsCheck.push("is-invalid");
      if (control.state === CtrlStates.VALID) _clsCheck.push("is-valid");

      _checked = (value === item.value);

      _click = () => {
        control.onValueChange(item.value || item.name) || this.forceUpdate();
      };
    
      if (item.custom) {
        return (
          <div key={key}  className={classnames(_clsInput, control.customClass)}>
            <div className={classnames(_clsCheck)}>
              <input
                id={_id}
                type={control.getType()}
                name={control.name}
                checked={_checked}
                onChange={_click}
                aria-invalid={control.state === CtrlStates.ERROR}
                disabled={item.disabled}/>
              <label htmlFor={_id} className={classnames(_clsLabel)}>
                {item.name}
              </label>
            </div>
          </div>
        );
      }

      return (
        <div key={key} className={classnames(_clsInput, control.customClass)}>
          <input
            id={_id}
            className={classnames(_clsCheck)}
            type={control.getType()}
            name={control.name}
            checked={_checked}
            onChange={_click}
            aria-invalid={control.state === CtrlStates.ERROR}
            disabled={item.disabled}/>
          <label htmlFor={_id} className={classnames(_clsLabel)}>
            {item.name}
          </label>
        </div>
      );
    });

    return options && optionGroup;
  }

  /**
   *
   * @param {Ctrl} control
   */
  _inputDropdown (control) {
    let _cls = ["form-control"];
    if (control.state === CtrlStates.ERROR) _cls.push("is-invalid");
    if (control.state === CtrlStates.VALID) _cls.push("is-valid");
    if (control.custom === true) _cls.push("custom-select");

    let _opt = control.options.map((option, key) => {
      return (
        <option value={option.value} key={key}>
          {option.name}
        </option>
      );
    });

    return (
      <select
        className={classnames(_cls, control.customClass)}
        onChange={e => control.onValueChange(e.target.value) || this.forceUpdate()}
        value={control.value || ""}>
        {_opt}
      </select>
    );
  }

  /**
   *
   * @param {Ctrl} control
   */
  _inputTextArea (control) {
    let _cls = ["form-control"];
    if (control.state === CtrlStates.ERROR) _cls.push("is-invalid");
    if (control.state === CtrlStates.VALID) _cls.push("is-valid");

    return (
      <textarea
        className={classnames(_cls, control.customClass)}
        value={control.value || ""}
        placeholder={control.placeholder}
        cols={control.cols || 30}
        rows={control.rows || 8}
        onChange={e => control.onValueChange(e.target.value) || this.forceUpdate()}/>
    );
  }
}

NextControlRenderer.defaultProps = {
  control: null,
  className: ""
};

NextControlRenderer.propTypes = {
  control: PropTypes.instanceOf(Ctrl).isRequired,
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string)
  ])
};
